import React, { useState } from "react";
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  Box,
  Paper,
  FormHelperText,
  Grid,
  Divider,
  Tooltip,
  CircularProgress, // Importar para el feedback de carga
} from "@mui/material";

// --- Función de Guardado (puede estar en otro archivo, ej: api.js) ---
const saveQuestionAPI = (questionData) => {
  console.log("Enviando datos a la API...", questionData);
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (Math.random() > 0.1) {
        // 90% de éxito
        const savedData = {
          ...questionData,
          id: Date.now(),
          createdAt: new Date().toISOString(),
        };
        console.log("API respondió con éxito:", savedData);
        resolve({
          success: true,
          data: savedData,
          message: "¡Pregunta guardada con éxito!",
        });
      } else {
        // 10% de error
        console.error("API respondió con un error.");
        reject({
          success: false,
          message: "Error de servidor: No se pudo guardar la pregunta.",
        });
      }
    }, 1500);
  });
};

const QuestionForm = () => {
  // ... todos los estados anteriores ...
  const [question, setQuestion] = useState("");
  const [answers, setAnswers] = useState([
    { text: "", isCorrect: false },
    { text: "", isCorrect: false },
    { text: "", isCorrect: false },
    { text: "", isCorrect: false },
  ]);
  const [category, setCategory] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [errors, setErrors] = useState({});

  // --- Nuevo estado para la carga ---
  const [isSaving, setIsSaving] = useState(false);

  // --- Función de reseteo ---
  const resetForm = () => {
    setQuestion("");
    setAnswers([
      { text: "", isCorrect: false },
      { text: "", isCorrect: false },
      { text: "", isCorrect: false },
      { text: "", isCorrect: false },
    ]);
    setCategory("");
    setDifficulty("");
    setErrors({});
  };

  // ... todas las funciones handle... sin cambios ...
  const formatInputText = (text) => {
    if (!text) return "";
    const trimmedText = text.trimStart();
    return trimmedText
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };
  const handleQuestionChange = (event) => {
    setQuestion(formatInputText(event.target.value));
    if (errors.question)
      setErrors((prevErrors) => ({ ...prevErrors, question: null }));
  };
  const handleAnswerChange = (index, event) => {
    const newAnswers = [...answers];
    const newText = formatInputText(event.target.value);
    newAnswers[index].text = newText;
    if (!newText.trim() && newAnswers[index].isCorrect)
      newAnswers[index].isCorrect = false;
    setAnswers(newAnswers);
    const errorKey = `answer_${index}`;
    if (errors[errorKey])
      setErrors((prevErrors) => ({ ...prevErrors, [errorKey]: null }));
  };
  const handleCategoryChange = (event) => {
    setCategory(event.target.value);
    if (errors.category)
      setErrors((prevErrors) => ({ ...prevErrors, category: null }));
  };
  const handleDifficultyChange = (event) => {
    setDifficulty(event.target.value);
    if (errors.difficulty)
      setErrors((prevErrors) => ({ ...prevErrors, difficulty: null }));
  };
  const handleCorrectAnswerChange = (selectedIndex, event) => {
    const newAnswers = answers.map((answer, index) => ({
      ...answer,
      isCorrect: index === selectedIndex ? event.target.checked : false,
    }));
    setAnswers(newAnswers);
    if (errors.correctAnswer)
      setErrors((prevErrors) => ({ ...prevErrors, correctAnswer: null }));
  };

  // --- FUNCIÓN DE VALIDACIÓN Y ENVÍO ACTUALIZADA ---
  const validateAndSubmit = async (event) => {
    event.preventDefault();
    setErrors({}); // Limpiar errores antiguos al intentar guardar de nuevo

    // ... lógica de validación (sin cambios) ...
    const newErrors = {};
    if (!question.trim()) newErrors.question = "Campo obligatorio";
    answers.forEach((answer, index) => {
      if (!answer.text.trim())
        newErrors[`answer_${index}`] = "Campo obligatorio";
    });
    const allAnswersAreFilled = answers.every(
      (answer) => answer.text.trim() !== ""
    );
    if (allAnswersAreFilled && !answers.some((answer) => answer.isCorrect)) {
      newErrors.correctAnswer = "Debes seleccionar una respuesta correcta";
    }
    if (!category) newErrors.category = "Campo obligatorio";
    if (!difficulty) newErrors.difficulty = "Campo obligatorio";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Si la validación pasa, comienza el proceso de guardado
    setIsSaving(true);
    const finalQuestion = {
      question: question.trim(),
      answers: answers.map((answer) => ({
        ...answer,
        text: answer.text.trim(),
      })),
      category,
      difficulty,
    };

    console.log(finalQuestion);

    try {
      const response = await saveQuestionAPI(finalQuestion);
      // Éxito: notificar al usuario y limpiar el formulario
      alert(response.message); // En una app real, usar un Snackbar
      resetForm();
    } catch (error) {
      // Error: notificar al usuario
      alert(error.message || "Ocurrió un error inesperado."); // En una app real, usar un Snackbar
    } finally {
      // Se ejecuta siempre, tanto en éxito como en error
      setIsSaving(false);
    }
  };

  const correctAnswerIndex = answers.findIndex((a) => a.isCorrect);
  const allAnswersFilled = answers.every((answer) => answer.text.trim() !== "");

  return (
    <Paper
      elevation={3}
      sx={{ padding: 4, margin: 4, maxWidth: "800px", mx: "auto", mt: 10 }}
    >
      <Typography variant="h5" component="h2" gutterBottom sx={{mb:3}}>
        <b>Crear Nueva Pregunta</b>
      </Typography>
      <form onSubmit={validateAndSubmit} noValidate>
        {/* ... campos del formulario ... (sin cambios) */}
        {/* ... */}
        <Box mb={3}>
          <TextField
            fullWidth
            label="Enunciado de la Pregunta"
            variant="outlined"
            value={question}
            onChange={handleQuestionChange}
            required
            error={!!errors.question}
            helperText={errors.question || ""}
          />
        </Box>
        <Typography variant="h6" component="h3" gutterBottom>
          <b>Respuestas</b>
        </Typography>
        <FormGroup>
          {answers.map((answer, index) => {
            const isCheckboxVisible =
              correctAnswerIndex === index ||
              (correctAnswerIndex === -1 && allAnswersFilled);
            return (
              <Box key={index} display="flex" alignItems="center" mb={2}>
                <TextField
                  fullWidth
                  label={`Respuesta ${index + 1}`}
                  variant="outlined"
                  value={answer.text}
                  onChange={(e) => handleAnswerChange(index, e)}
                  required
                  sx={{
                    mr: 2,
                    ...(answer.isCorrect && {
                      "& .MuiInputLabel-root": { color: "success.main" },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "success.main",
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "success.main",
                          borderWidth: "2px",
                        },
                        "&:hover fieldset": {
                          borderColor: "success.main",
                          borderWidth: "2px",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "success.main",
                          borderWidth: "2px",
                        },
                      },
                    }),
                  }}
                  error={!!errors[`answer_${index}`]}
                  helperText={errors[`answer_${index}`] || ""}
                />
                {isCheckboxVisible && (
                  <Tooltip
                    title="Marcar como respuesta correcta"
                    placement="right"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="success"
                          checked={answer.isCorrect}
                          onChange={(e) => handleCorrectAnswerChange(index, e)}
                          disabled={
                            correctAnswerIndex !== -1 &&
                            correctAnswerIndex !== index
                          }
                        />
                      }
                      label={answer.isCorrect ? "Correcta" : ""}
                      sx={{
                        ...(answer.isCorrect && { color: "success.main" }),
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          })}
          {errors.correctAnswer && (
            <FormHelperText error sx={{ mt: -1, mb: 2 }}>
              {errors.correctAnswer}
            </FormHelperText>
          )}
        </FormGroup>
        <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
          <FormControl
            fullWidth
            variant="outlined"
            required
            error={!!errors.category}
          >
            <InputLabel id="category-label">Categoría</InputLabel>
            <Select
              labelId="category-label"
              value={category}
              onChange={handleCategoryChange}
              label="Categoría"
            >
              <MenuItem value="">
                <em>Seleccione una</em>
              </MenuItem>
              <MenuItem value="Salud">Salud</MenuItem>
              <MenuItem value="Nutrición">Nutrición</MenuItem>
              <MenuItem value="WASH">WASH</MenuItem>
            </Select>
            <FormHelperText>{errors.category || ""}</FormHelperText>
          </FormControl>
          <FormControl
            fullWidth
            variant="outlined"
            required
            error={!!errors.difficulty}
          >
            <InputLabel id="difficulty-label">Dificultad</InputLabel>
            <Select
              labelId="difficulty-label"
              value={difficulty}
              onChange={handleDifficultyChange}
              label="Dificultad"
            >
              <MenuItem value="">
                <em>Seleccione una</em>
              </MenuItem>
              <MenuItem value="Basico">Básico</MenuItem>
              <MenuItem value="Intermedio">Intermedio</MenuItem>
              <MenuItem value="Avanzado">Avanzado</MenuItem>
              <MenuItem value="Experto">Experto</MenuItem>
            </Select>
            <FormHelperText>{errors.difficulty || ""}</FormHelperText>
          </FormControl>
        </Box>
        {/* ... */}

        <Divider sx={{ my: 4 }} />

        <Grid container justifyContent="center">
          <Grid item sm={6} xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                position: "relative",
              }}
            >
              <Button
                variant="contained"
                color="error"
                size="large"
                onClick={resetForm}
                disabled={isSaving} // Deshabilitar mientras se guarda
                sx={{ flexGrow: 1 }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isSaving} // Deshabilitar mientras se guarda
                sx={{
                  backgroundColor: "teal",
                  "&:hover": { backgroundColor: "#00695c" },
                  flexGrow: 1,
                }}
              >
                {isSaving ? "Guardando..." : "Guardar"}
              </Button>
              {isSaving && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: "teal",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default QuestionForm;
